import './App.css';
import './casino.css';
import './minimal.css';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useLaserEyes } from '@omnisat/lasereyes-react';
import { Suspense, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { lazyWithReload as lazy } from './utils/lazyWithReload';
import ConnectWalletModal from './components/ConnectWalletModal';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import ErrorBoundary from './components/ErrorBoundary';
import { RouteFallback } from './components/Spinner';
import casinoTheme from './casinoTheme';
import { BlockHeightProvider } from './context/BlockHeightContext';
import { LanguageProvider, useLang } from './context/LanguageContext';
import { I18nLock } from './components/I18nLock';
import ConnectContext from './context/ConnectContext';
import { ToastProvider } from './components/Toast';
import { XMark } from './config/design';
import { useAllTokens } from './hooks/useWalletBalance';
import { isArbuzToken, getArbuzBalance, isDieselToken, getDieselBalance, formatDiesel } from './utils/arbuzUtils';
import { useIsMobile } from './hooks/useIsMobile';
import { MobLayout } from './mobile/MobLayout';
import { MobConnectModal } from './mobile/MobConnectModal';

// Route-level code splitting: each page (and the heavy tx/wallet libs only the mint
// pages pull in) loads on demand instead of inflating the main bundle.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Send = lazy(() => import('./pages/Send'));
const Cards = lazy(() => import('./pages/Cards'));
const Inugami = lazy(() => import('./pages/Inugami'));
const Activity = lazy(() => import('./pages/Activity'));
const Arbuzino = lazy(() => import('./pages/Arbuzino'));
// Hidden operator page — reachable only by typing /admin (never added to NAV_PATHS).
const Admin = lazy(() => import('./pages/Admin'));
const MobDashboard = lazy(() => import('./mobile/pages/MobDashboard').then(m => ({ default: m.MobDashboard })));
const MobSend = lazy(() => import('./mobile/pages/MobSend').then(m => ({ default: m.MobSend })));
const MobCards = lazy(() => import('./mobile/pages/MobCards').then(m => ({ default: m.MobCards })));
const MobInugami = lazy(() => import('./mobile/pages/MobInugami').then(m => ({ default: m.MobInugami })));
const MobActivity = lazy(() => import('./mobile/pages/MobActivity').then(m => ({ default: m.MobActivity })));
const MobArbuzino = lazy(() => import('./mobile/pages/MobArbuzino').then(m => ({ default: m.MobArbuzino })));

// RouteFallback (the shared spinner) now lives in ./components/Spinner.

const NAV_PATHS = [
  { key: 'nav_arbuzino', path: '/' },
  { key: 'nav_mint',     path: '/mint' },
  { key: 'nav_card',     path: '/send' },
  { key: 'nav_deck',     path: '/cards' },
  { key: 'nav_activity', path: '/activity', ping: true },
  { key: 'nav_inugami',  path: '/inugami' },
];

function TopBar({ connected, address, onConnect, onDisconnect }) {
  const { t, lang, setLang } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // total ARBUZ across all holdings (react-query cached, shared with Dashboard)
  const { tokens: allTokens, loading: arbuzLoading } = useAllTokens();
  const totalArbuz = allTokens.filter(isArbuzToken).reduce((sum, tk) => sum + getArbuzBalance(tk), 0);
  const totalDiesel = allTokens.filter(isDieselToken).reduce((sum, tk) => sum + getDieselBalance(tk), 0);

  const activeIdx = NAV_PATHS.findIndex(n => n.path === '/' ? pathname === '/' : pathname.startsWith(n.path));
  const itemRefs = useRef([]);
  const navRef = useRef(null);
  const [hl, setHl] = useState({ left: 0, width: 0, ready: false, fly: false });
  // The pill flies only between two VISIBLE positions. Coming from a hidden state
  // (Arbuzino's foil tab, or first paint) it must snap into place and just fade in —
  // otherwise it streaks across the whole nav from a stale/zero position. The flag is
  // captured at measure time (not derived at render) so the snap lands before any fly.
  const hlWasVisible = useRef(false);
  useLayoutEffect(() => {
    // Arbuzino has its own animated foil pill — don't slide the gold highlight onto
    // it. Leave the highlight where it was so it just fades out in place (no fly).
    // `fly` is decided ONCE per tab change: the fonts/ResizeObserver re-measures below
    // reuse it, so a same-tab refinement can never flip a snap into a mid-paint fly.
    const fly = hlWasVisible.current;
    hlWasVisible.current = true;
    const measure = () => {
      const el = itemRefs.current[activeIdx];
      if (el) setHl({ left: el.offsetLeft, width: el.offsetWidth, ready: true, fly });
    };
    measure();
    // webfonts load async → tab widths shift after first paint; re-measure so the pill stays aligned
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});
    // catch any later reflow of the nav (resize, language width change)
    const ro = (navRef.current && 'ResizeObserver' in window) ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(navRef.current);
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [activeIdx, t]);

  const navLinkStyle = (isActive) => ({
    position: 'relative',
    zIndex: 1,
    fontWeight: 700,
    fontSize: '13px',
    color: isActive ? 'var(--ink)' : 'var(--dim)',
    textDecoration: 'none',
    padding: '7px 12px',
    borderRadius: '999px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'color .25s ease, transform .12s cubic-bezier(.34,1.5,.6,1)',
  });

  return (
    <header style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '0 30px',
      height: '64px',
      background: 'var(--bg)',
      borderBottom: '1px solid var(--rule)',
      position: 'sticky',
      top: 0,
      zIndex: 40,
    }}>

      {/* Brand */}
      <div onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 8, marginRight: 14, cursor: 'pointer', flexShrink: 0, fontFamily: 'var(--f-body)', fontWeight: 800, fontSize: 15, letterSpacing: '-0.2px', color: 'var(--ink)' }}>
        <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 7, background: 'var(--melon)', display: 'grid', placeItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />
        </span>
        Magic Arbuz
      </div>

      {/* Nav */}
      <nav ref={navRef} style={{ display: 'flex', gap: '4px', position: 'relative', flexShrink: 0 }}>
        {/* sliding highlight */}
        <div style={{
          position: 'absolute', top: 0, bottom: 0,
          left: hl.left, width: hl.width,
          borderRadius: '999px',
          background: 'var(--yellow)',
          opacity: hl.ready ? 1 : 0,
          transition: hl.fly
            ? 'left .34s cubic-bezier(.34,1.4,.5,1), width .34s cubic-bezier(.34,1.4,.5,1), opacity .2s'
            : 'opacity .2s',
          pointerEvents: 'none', zIndex: 0,
        }} />
        {NAV_PATHS.map((n, i) => {
          const isActive = n.path === '/' ? pathname === '/' : pathname.startsWith(n.path);
          return (
            <div key={n.path} ref={el => itemRefs.current[i] = el} className="navlink" onClick={() => navigate(n.path)} style={navLinkStyle(isActive)}>
              <I18nLock t={t} k={n.key} />
              {n.ping && (
                <span style={{
                  position: 'absolute', top: 3, right: 6, width: 7, height: 7, borderRadius: '50%',
                  background: 'var(--rind)', animation: 'ocpulse 1.1s infinite',
                }} />
              )}
            </div>
          );
        })}
      </nav>

      <div style={{ flex: 1 }} />

      {/* Holdings: DIESEL + ARBUZ (left of the language toggle) */}
      {connected && (
        <div className="hdr-holdings" style={{ display: 'flex', alignItems: 'center', gap: 16, marginRight: 14, flexShrink: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', lineHeight: 1.15 }}>
            <span style={{ fontFamily: 'var(--f-body)', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--faint)' }}>
              DIESEL
            </span>
            <span style={{ fontFamily: 'var(--f-mono)', fontSize: 15, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
              {arbuzLoading ? '…' : formatDiesel(totalDiesel)}
            </span>
          </div>
          <div style={{ width: 1, alignSelf: 'stretch', margin: '3px 0', background: 'var(--rule)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', lineHeight: 1.15 }}>
            <span style={{ fontFamily: 'var(--f-body)', fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: 'var(--faint)' }}>
              ARBUZ
            </span>
            <span style={{ fontFamily: 'var(--f-mono)', fontSize: 15, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
              {arbuzLoading ? '…' : String(Math.floor(totalArbuz))}
            </span>
          </div>
        </div>
      )}

      {/* Language switch - one button (binary EN/中文 choice is just a flip). Both
          labels are ghosted (neither is "the default") so the button never resizes
          itself when it flips. */}
      <button
        className="lang-btn"
        style={{ marginRight: '10px' }}
        aria-label={lang === 'en' ? 'Switch to 中文' : 'Switch to English'}
        onClick={() => setLang(lang === 'en' ? 'zh' : 'en')}
      >
        <I18nLock ghosts={['中文', 'EN']}>{lang === 'en' ? '中文' : 'EN'}</I18nLock>
      </button>

      {/* Wallet button */}
      <button
        onClick={connected ? onDisconnect : onConnect}
        style={{
          fontFamily: connected ? 'var(--f-mono)' : 'var(--f-body)',
          fontWeight: 700,
          fontSize: '13px',
          color: 'var(--ink)',
          cursor: 'pointer',
          background: 'var(--card)',
          padding: '9px 16px',
          borderRadius: '999px',
          border: 'none',
          boxShadow: '0 1px 4px rgba(38, 32, 26, 0.12)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
          transition: 'transform 0.1s',
        }}
        onMouseDown={(e) => { if (!connected) e.currentTarget.style.transform = 'translateY(3px)'; }}
        onMouseUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        {connected ? `${address.slice(0, 8)}…${address.slice(-6)}` : <I18nLock t={t} k="nav_connectWallet" />}
      </button>
    </header>
  );
}

function App() {
  const { connected, address, connect, disconnect } = useLaserEyes();
  const [openConnectModal, setOpenConnectModal] = useState(false);
  const isMobile = useIsMobile();

  const handleConnect = () => setOpenConnectModal(true);

  // Keep the modal open until the wallet actually connects: the modal shows its own
  // connecting spinner / error, and re-throws so the modal can surface a failed/rejected
  // attempt. The modal closes itself reactively once `connected` flips true (below).
  const handleWalletConnect = async (walletConstant) => {
    await connect(walletConstant);
  };

  // close the connect modal once a wallet is actually connected
  useEffect(() => {
    if (connected) setOpenConnectModal(false);
  }, [connected]);

  const handleDisconnect = async () => {
    try { await disconnect(); } catch {}
  };

  const connectModal = isMobile ? (
    <MobConnectModal
      open={openConnectModal}
      onClose={() => setOpenConnectModal(false)}
      onConnect={handleWalletConnect}
    />
  ) : (
    <ConnectWalletModal
      open={openConnectModal}
      onClose={() => setOpenConnectModal(false)}
      onConnect={handleWalletConnect}
    />
  );

  return (
    <ErrorBoundary>
      <LanguageProvider>
      <BlockHeightProvider>
      <ConnectContext.Provider value={handleConnect}>
      <ThemeProvider theme={casinoTheme}>
        <CssBaseline />
        <Analytics />
        <SpeedInsights />
        <ToastProvider>
        {isMobile ? (
          <MobLayout openConnectModal={connectModal}>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<MobArbuzino />} />
                <Route path="/mint" element={<MobDashboard onConnect={handleConnect} />} />
                <Route path="/send" element={<MobSend />} />
                <Route path="/cards" element={<MobCards />} />
                <Route path="/activity" element={<MobActivity />} />
                <Route path="/inugami" element={<MobInugami />} />
                <Route path="/arbuzino" element={<Navigate to="/" replace />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </Suspense>
          </MobLayout>
        ) : (
          <>
            <TopBar
              connected={connected}
              address={address}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
            />
            {connectModal}
            {/* Everything below the header scrolls inside .scroll-area (not the document):
                a non-root scroller keeps the scrollbar out of the header, gives it a
                transparent track (felt shows through), and reserves its gutter so page
                cabinets don't shift between tabs that do/don't overflow. See App.css. */}
            <div className="scroll-area">
              <main style={{ width: '100%', maxWidth: 1120, margin: '0 auto', padding: '26px 24px 34px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    <Route path="/" element={<Arbuzino />} />
                    <Route path="/mint" element={<Dashboard />} />
                    <Route path="/send" element={<Send />} />
                    <Route path="/cards" element={<Cards />} />
                    <Route path="/activity" element={<Activity />} />
                    <Route path="/inugami" element={<Inugami />} />
                    <Route path="/arbuzino" element={<Navigate to="/" replace />} />
                    <Route path="/admin" element={<Admin />} />
                  </Routes>
                </Suspense>
              </main>
              <footer className="site-footer">
                <div className="sf-rule" />
                <a href="https://x.com/Fartmaxxximus" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                  <XMark size={16} />
                </a>
              </footer>
            </div>
          </>
        )}
        </ToastProvider>
      </ThemeProvider>
      </ConnectContext.Provider>
      </BlockHeightProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
